import { ReactNode, useMemo } from "react";
import { Alert, Button, Group, Stack, Text } from "@mantine/core";
import {
  IconAlertTriangle,
  IconCircleCheck,
  IconInfoCircle,
} from "@tabler/icons-react";
import type {
  AggregateState,
  RecallState,
} from "../../services/reportDeliveryRecall.service";

interface SignedOffDeliveryAlertProps {
  state: RecallState | undefined;
  recallAction?: ReactNode;
  deliveryDetailsAction?: ReactNode;
  checking?: boolean;
  unknownOutcome?: boolean;
  stateUnavailable?: boolean;
  onRefreshState?: () => void;
}

const COPY: Record<AggregateState, { title: string; message: string }> = {
  none: {
    title: "Case Signed Off",
    message:
      "This case is read-only. The report has not been queued for delivery.",
  },
  queued: {
    title: "Case Signed Off",
    message: "This case is read-only. The report is queued for delivery.",
  },
  recalled: {
    title: "Report recalled",
    message: "Pending report deliveries were stopped. Nothing was sent.",
  },
  partial_recall: {
    title: "Partial recall",
    message:
      "Some deliveries were stopped and some could not be stopped. Follow up immediately.",
  },
  sending: {
    title: "Report sending",
    message: "Delivery has started and can no longer be recalled.",
  },
  delivered: {
    title: "Report delivered",
    message: "The signed report has been delivered.",
  },
  failed: {
    title: "Report delivery failed",
    message:
      "The report was not delivered. Review the delivery queue for details.",
  },
  cancelled: {
    title: "Report delivery cancelled",
    message: "The queued delivery was cancelled.",
  },
  mixed: {
    title: "Mixed delivery state",
    message:
      "Report destinations have different delivery outcomes. Review the details.",
  },
  incomplete: {
    title: "Delivery information incomplete",
    message:
      "Some delivery information is unavailable. Review the delivery details.",
  },
};

export function SignedOffDeliveryAlert({
  state,
  recallAction,
  deliveryDetailsAction,
  checking = false,
  unknownOutcome = false,
  stateUnavailable = false,
  onRefreshState,
}: SignedOffDeliveryAlertProps) {
  const aggregateState = useMemo<AggregateState>(
    () => state?.aggregateState ?? "none",
    [state?.aggregateState]
  );
  const content = useMemo(() => {
    if (unknownOutcome) {
      return {
        title: "Checking recall outcome",
        message:
          "The report may still send while the system verifies the request. Do not assume it was recalled.",
      };
    }
    if (stateUnavailable) {
      return {
        title: "Delivery status unavailable",
        message:
          "The current delivery status could not be loaded. Refresh the case before relying on this status.",
      };
    }
    if (checking) {
      return {
        title: "Checking delivery status",
        message: "Confirming whether the queued report can still be recalled.",
      };
    }
    return COPY[aggregateState];
  }, [aggregateState, checking, stateUnavailable, unknownOutcome]);
  const color = useMemo(() => {
    if (
      unknownOutcome ||
      stateUnavailable ||
      checking ||
      aggregateState === "partial_recall"
    ) {
      return "orange";
    }
    if (aggregateState === "failed") return "red";
    if (
      aggregateState === "queued" ||
      aggregateState === "recalled" ||
      aggregateState === "delivered"
    ) {
      return "green";
    }
    return "blue";
  }, [aggregateState, checking, stateUnavailable, unknownOutcome]);

  return (
    <Alert
      color={color}
      variant="light"
      icon={
        color === "green" ? (
          <IconCircleCheck size={18} />
        ) : color === "orange" || color === "red" ? (
          <IconAlertTriangle size={18} />
        ) : (
          <IconInfoCircle size={18} />
        )
      }
      title={content.title}
      role={
        unknownOutcome ||
        stateUnavailable ||
        aggregateState === "partial_recall"
          ? "alert"
          : "status"
      }
    >
      <Group justify="space-between" align="center" wrap="wrap">
        <Stack gap={2}>
          <Text size="sm">{content.message}</Text>
        </Stack>
        {stateUnavailable && onRefreshState ? (
          <Button
            variant="light"
            color="orange"
            onClick={onRefreshState}
            style={{ minHeight: 44 }}
          >
            Refresh status
          </Button>
        ) : (
          <Group gap="sm" wrap="wrap">
            {state?.canRecall && recallAction}
            {deliveryDetailsAction}
          </Group>
        )}
      </Group>
    </Alert>
  );
}
